import React, { useState } from 'react';
import { TargetIcon, BellIcon, ShieldIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
interface CampaignsSegmentationProps {
  patientId: number;
}
export function CampaignsSegmentation({
  patientId
}: CampaignsSegmentationProps) {
  const [consentSettings, setConsentSettings] = useState({
    marketingEmails: true,
    marketingSMS: false,
    healthReminders: true,
    appointmentReminders: true,
    vaccinationAlerts: true,
    promotionalOffers: false,
    surveyParticipation: true
  });
  const campaigns = [{
    id: 1,
    name: 'Flu Vaccination Campaign 2024',
    type: 'Vaccination Alert',
    status: 'Enrolled',
    nextAction: 'Schedule appointment',
    dueDate: '2024-02-15'
  }, {
    id: 2,
    name: 'Diabetes Management Program',
    type: 'Health Campaign',
    status: 'Active',
    nextAction: 'Monthly check-in',
    dueDate: '2024-01-25'
  }, {
    id: 3,
    name: 'Annual Health Checkup Reminder',
    type: 'Follow-up Reminder',
    status: 'Pending',
    nextAction: 'Book appointment',
    dueDate: '2024-03-01'
  }];
  const segments = [{
    name: 'Chronic Disease Management',
    enrolled: true,
    description: 'Patients with ongoing chronic conditions'
  }, {
    name: 'Preventive Care Focus',
    enrolled: true,
    description: 'Regular health screenings and vaccinations'
  }, {
    name: 'Senior Care Program',
    enrolled: false,
    description: 'Specialized care for patients 65+'
  }, {
    name: 'Wellness Initiatives',
    enrolled: true,
    description: 'Health and wellness campaigns'
  }];
  const upcomingReminders = [{
    id: 1,
    type: 'Vaccination',
    message: 'Flu shot due',
    date: '2024-02-15',
    sent: false
  }, {
    id: 2,
    type: 'Follow-up',
    message: 'Diabetes check-up',
    date: '2024-01-25',
    sent: false
  }, {
    id: 3,
    type: 'Medication',
    message: 'Prescription refill reminder',
    date: '2024-01-20',
    sent: true
  }];
  const toggleConsent = (key: keyof typeof consentSettings) => {
    setConsentSettings({
      ...consentSettings,
      [key]: !consentSettings[key]
    });
  };
  return <div className="space-y-6" data-id="element-620">
      {/* Consent & Privacy Settings */}
      <div className="border border-gray-200 rounded-lg p-6" data-id="element-621">
        <div className="flex items-center space-x-2 mb-4" data-id="element-622">
          <ShieldIcon className="w-6 h-6 text-blue-600" data-id="element-623" />
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-624">
            Consent & Privacy Settings
          </h3>
        </div>
        <p className="text-sm text-gray-600 mb-4" data-id="element-625">
          Manage communication preferences and privacy settings
        </p>
        <div className="space-y-3" data-id="element-626">
          {Object.entries(consentSettings).map(([key, value]) => <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-627">
              <div className="flex items-center space-x-3" data-id="element-628">
                {value ? <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-629" /> : <XCircleIcon className="w-5 h-5 text-gray-400" data-id="element-630" />}
                <span className="text-sm font-medium text-gray-900" data-id="element-631">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" data-id="element-632">
                <input type="checkbox" checked={value} onChange={() => toggleConsent(key as keyof typeof consentSettings)} className="sr-only peer" data-id="element-633" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" data-id="element-634"></div>
              </label>
            </label>)}
        </div>
        <div className="mt-4 flex justify-end" data-id="element-635">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-636">
            Save Preferences
          </button>
        </div>
      </div>
      {/* Patient Segments */}
      <div className="border border-gray-200 rounded-lg p-6" data-id="element-637">
        <div className="flex items-center space-x-2 mb-4" data-id="element-638">
          <TargetIcon className="w-6 h-6 text-purple-600" data-id="element-639" />
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-640">
            Patient Segments
          </h3>
        </div>
        <div className="space-y-3" data-id="element-641">
          {segments.map((segment, idx) => <div key={idx} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-642">
              <div className="flex-1" data-id="element-643">
                <h4 className="font-medium text-gray-900" data-id="element-644">{segment.name}</h4>
                <p className="text-sm text-gray-600" data-id="element-645">{segment.description}</p>
              </div>
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${segment.enrolled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-646">
                {segment.enrolled ? 'Enrolled' : 'Not Enrolled'}
              </span>
            </div>)}
        </div>
      </div>
      {/* Active Campaigns */}
      <div className="border border-gray-200 rounded-lg p-6" data-id="element-647">
        <div className="flex items-center justify-between mb-4" data-id="element-648">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-649">
            Active Campaigns
          </h3>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm" data-id="element-650">
            Enroll in Campaign
          </button>
        </div>
        <div className="space-y-3" data-id="element-651">
          {campaigns.map(campaign => <div key={campaign.id} className="border border-gray-200 rounded-lg p-4" data-id="element-652">
              <div className="flex items-start justify-between mb-2" data-id="element-653">
                <div data-id="element-654">
                  <h4 className="font-semibold text-gray-900" data-id="element-655">
                    {campaign.name}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-656">{campaign.type}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${campaign.status === 'Active' ? 'bg-green-100 text-green-800' : campaign.status === 'Enrolled' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-657">
                  {campaign.status}
                </span>
              </div>
              <div className="text-sm text-gray-700" data-id="element-658">
                <p data-id="element-659">
                  <strong data-id="element-660">Next Action:</strong> {campaign.nextAction}
                </p>
                <p data-id="element-661">
                  <strong data-id="element-662">Due Date:</strong> {campaign.dueDate}
                </p>
              </div>
            </div>)}
        </div>
      </div>
      {/* Upcoming Reminders */}
      <div className="border border-gray-200 rounded-lg p-6" data-id="element-663">
        <div className="flex items-center space-x-2 mb-4" data-id="element-664">
          <BellIcon className="w-6 h-6 text-orange-600" data-id="element-665" />
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-666">
            Upcoming Reminders
          </h3>
        </div>
        <div className="space-y-3" data-id="element-667">
          {upcomingReminders.map(reminder => <div key={reminder.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg" data-id="element-668">
              <div className="flex-1" data-id="element-669">
                <div className="flex items-center space-x-2 mb-1" data-id="element-670">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800" data-id="element-671">
                    {reminder.type}
                  </span>
                  <h4 className="font-medium text-gray-900" data-id="element-672">
                    {reminder.message}
                  </h4>
                </div>
                <p className="text-sm text-gray-600" data-id="element-673">
                  Scheduled: {reminder.date}
                </p>
              </div>
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${reminder.sent ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-674">
                {reminder.sent ? 'Sent' : 'Pending'}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}